import { useState, useRef, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import searchService from '@api/v1/searchService'
import userService from '@api/v1/userService'
import { REPORTS_ONLY_MODE, HOME_ROUTE } from '@config/featureFlags'
import { getTenant, isSharedHost, clearStoredSite } from '@config/tenant'
import logoDark from '../../assets/logo-dark.svg'
import NotificationCenter from '../reports/NotificationCenter'
import ReportCenterNav from '../reports/ReportCenterNav'

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/matters': 'Matters',
  '/contacts': 'Contacts',
  '/calendar': 'Calendar',
  '/time-entries': 'Time Entries',
  '/invoices': 'Invoices',
  '/payments': 'Payments',
  '/documents': 'Documents',
  '/reports': 'Report Center',
  // Exact-path entries: the fallback below keys off the first path segment, which
  // would otherwise title both of these "Report Center".
  '/reports/queue': 'Report Queue',
  '/reports/queue/schedules': 'Recurring schedules',
  '/reports/queue/packs': 'Report packs',
  '/settings': 'Settings',
}

const typeRoutes = {
  matter: '/matters',
  contact: '/contacts',
  1001: '/matters',
  3: '/contacts',
}

const Header = () => {
  const { user, logout } = useAuth0()
  const { data: meData } = useQuery({
    queryKey: ['users-me'],
    queryFn: () => userService.getMe(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const displayName = meData?.data?.displayName || user?.name || 'User'
  const email = meData?.data?.email || user?.email || ''
  const location = useLocation()
  const navigate = useNavigate()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [activeResultIndex, setActiveResultIndex] = useState(-1)
  const menuRef = useRef(null)
  const searchRef = useRef(null)

  const pathBase = '/' + (location.pathname.split('/')[1] || '')
  const pageTitle = pageTitles[location.pathname] || pageTitles[pathBase] || 'Centerbase'
  const site = getTenant()

  // Reports-only mode: a report search lives here in the header and drives the
  // Report Center catalog via the ?q= param (single source of truth — Reports.jsx
  // reads it). Searching from a report view sends you back to the filtered catalog.
  const [searchParams, setSearchParams] = useSearchParams()
  const reportQuery = searchParams.get('q') || ''
  const onReportSearch = (val) => {
    if (location.pathname === '/reports') {
      setSearchParams(val ? { q: val } : {}, { replace: true })
    } else {
      navigate(val ? `/reports?q=${encodeURIComponent(val)}` : '/reports')
    }
  }

  const { data: searchResults, isFetching } = useQuery({
    queryKey: ['globalSearch', searchTerm],
    queryFn: () => searchService.quickSearch(searchTerm),
    enabled: searchTerm.length >= 2,
    staleTime: 30000,
  })

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowUserMenu(false)
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleResultClick = (result) => {
    setShowResults(false)
    setSearchTerm('')
    setActiveResultIndex(-1)
    const route = typeRoutes[result.type] || typeRoutes[result.typeId]
    if (route && result.id) {
      navigate(`${route}/${result.id}`)
    }
  }

  const handleSearchKeyDown = (e) => {
    if (!showResults || !searchResults || searchResults.length === 0) {
      if (e.key === 'Escape') {
        setShowResults(false)
        setActiveResultIndex(-1)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveResultIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveResultIndex((prev) =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (activeResultIndex >= 0 && searchResults[activeResultIndex]) {
          handleResultClick(searchResults[activeResultIndex])
        }
        break
      case 'Escape':
        setShowResults(false)
        setActiveResultIndex(-1)
        break
    }
  }

  const isReportsRoute = location.pathname.startsWith('/reports')

  /*
   * Report search (reports-only) — drives the Report Center via ?q=.
   *
   * The placeholder names clients and matters because the search genuinely
   * reaches them: `Reports.jsx` runs the same `?q=` through `quickSearch` and
   * renders an "In your data" strip of matched matters/contacts (Layer B), each
   * offering the type-appropriate reports. Only claim the scope the page can
   * actually serve — if that strip is ever removed, this string goes back to
   * "Search reports…" in the same change.
   */
  const reportSearchBox = (
    <div className="relative w-40 sm:w-56 lg:w-72">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>
      {/* ADA (2026-08-26): border-rc-control (#8b95a5), not border-gray-200.
          This field's border was the single worst ratio measured in the app —
          #e5e7eb on the bg-gray-50 it is filled with is 1.19:1, below even the
          1.24:1 the same grey scores on white. The border is the only thing
          drawing this control, so SC 1.4.11 wants 3.0:1 from it. The pill
          radius (rounded-full) is deliberate and stays: B2 normalises BUTTON
          radius, and this is the mock's search field, not a button. */}
      <input
        type="text"
        placeholder="Search reports, clients, matters…"
        aria-label="Search reports"
        value={reportQuery}
        onChange={(e) => onReportSearch(e.target.value)}
        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-rc-control rounded-full text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent transition-colors"
      />
    </div>
  )

  // User menu — avatar-only trigger in reports-only (name/email live in the dropdown);
  // legacy shell keeps the inline name/email next to the avatar.
  const userMenu = (
    <div className="relative" ref={menuRef}>
      <button
        aria-label="User menu"
        aria-expanded={showUserMenu}
        aria-haspopup="menu"
        onClick={() => setShowUserMenu(!showUserMenu)}
        className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-50 transition-colors"
      >
        <span className="w-8 h-8 rounded-full bg-cb-navy flex items-center justify-center text-white text-xs font-semibold">
          {displayName[0]?.toUpperCase() || 'U'}
        </span>
        {!REPORTS_ONLY_MODE && (
          <span className="text-left hidden sm:block">
            <span className="block text-sm font-medium text-gray-900 leading-tight">{displayName}</span>
            <span className="block text-xs text-gray-500 leading-tight">{email}</span>
          </span>
        )}
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {showUserMenu && (
        <div role="menu" className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">{displayName}</p>
            <p className="text-xs text-gray-500 truncate">{email}</p>
          </div>
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setShowUserMenu(false)}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Settings
          </Link>
          {isSharedHost() && (
            <button
              role="menuitem"
              onClick={() => {
                // Switch site: clear the remembered site + cached Auth0 session
                // (per project convention — avoids Auth0 logout-URL issues) and
                // return to the site-name prompt.
                clearStoredSite()
                Object.keys(localStorage)
                  .filter((k) => k.startsWith('@@auth0spajs@@'))
                  .forEach((k) => localStorage.removeItem(k))
                window.location.href = window.location.origin
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Switch site
            </button>
          )}
          <button
            role="menuitem"
            onClick={() => {
              // Forget the site on the way out (CC-53416). Sign-out is the one
              // moment the user has said "I'm done with this machine", and a
              // remembered site is what pins the NEXT person — a CSM, a partner,
              // anyone on a shared laptop — to this firm's Auth0 org with no
              // rendered way back to the site field. The cost is retyping a short
              // site name after an explicit sign-out; the alternative is the dead
              // end this ticket is about.
              if (isSharedHost()) clearStoredSite()
              logout({ logoutParams: { returnTo: window.location.origin } })
            }}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )

  // Reports-only header: [logo + stacked site badge] · [centered section title] ·
  // [report search + avatar]. The centered "Report Center." is brand chrome (a span,
  // not a heading) so pages keep exactly one h1. Non-/reports pages reachable by URL
  // get their h1 from here (an sr-only title); /reports* own their heading.
  if (REPORTS_ONLY_MODE) {
    /*
     * One LEFT-ALIGNED row, per the Figma top bar: logo · tenant · divider ·
     * "Report Center" · nav, then search / notifications / account on the right.
     *
     * Previously this was a 3-column grid with the tenant badge stacked UNDER the
     * logo and a second, centred brand lockup carrying the pillar dots. The mock
     * has neither: one row, one brand mark, the tenant inline beside it. Dropping
     * the centred lockup also stops "Report Center" appearing twice in the same
     * bar once the nav arrived.
     */
    return (
      /*
       * A1-scope (2026-08-26, Jordan's explicit call) — the BAND is full-bleed,
       * its CONTENTS follow the report gutter.
       *
       * The white strip, its bottom rule and its px-6 stay on <header> so the
       * band still spans the window; everything inside moves into a
       * `.report-shell` row. That is the same class `<main>`'s report pages use,
       * so the tab strip, the breadcrumb below and the report content share ONE
       * left edge at every viewport — reused, not re-pasted, because pasting the
       * utilities is exactly what let these three drift apart in the first place.
       *
       * The arithmetic works because `.report-shell` is written to sit inside a
       * 24px-padded parent (main's p-6): here the band's own px-6 plays that
       * part, so both routes total 24 + 58 = 82px (>=640px) and 24 + 66 = 90px
       * (>=1280px). Before this, the tab strip started at 24px and the report
       * title at 40px — and the A1 gutter increase would have widened that step
       * to ~66px.
       *
       * This reaches every route that renders Layout, not just Report Center —
       * accepted deliberately. A non-report page still puts its own content at
       * main's 24px, so on those pages the header now sits further in than the
       * body; they are unreachable in the product under REPORTS_ONLY_MODE and
       * widening them is the separate call spec 092 already deferred.
       */
      <header className="h-16 bg-white border-b border-gray-200 flex px-6 shrink-0">
        <div className="report-shell flex items-center gap-3">
          {!isReportsRoute && <h1 className="sr-only">{pageTitle}</h1>}

          {/* The logo is the home affordance — so "Report Center" beside it is a
              LABEL, not a second link to the same place. */}
          <Link to={HOME_ROUTE} aria-label="Centerbase — report center home" className="flex items-center rounded">
            <img src={logoDark} alt="" className="h-6 w-auto" />
          </Link>
          {site && (
            <span
              className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-semibold leading-none text-rc-ink-2"
              title={`Site: ${site}`}
            >
              {site}
            </span>
          )}
          <span aria-hidden="true" className="mx-1 h-5 w-px bg-rc-line" />
          <span className="font-display whitespace-nowrap text-[15px] leading-none text-rc-ink">
            Report Center
          </span>

          {/* Dashboards | Reports | Queue — the Figma puts this group in the top
              bar. `ReportCenterNav` derives it once from the route and returns null
              outside `/reports`, so the 32 non-report routes are unaffected. */}
          <ReportCenterNav />

          <div className="ml-auto flex items-center gap-3">
            {reportSearchBox}
            {/* Not in the mock, kept deliberately: the bell is newer than the
                design and is the only surface that tells you a queued report has
                finished. Same call as keeping the Run split button and the Queue
                tab, both of which post-date these frames. */}
            <NotificationCenter />
            {userMenu}
          </div>
        </div>
      </header>
    )
  }

  // Legacy full-shell header (flag OFF): page title + global cross-entity search.
  // Same band/contents split as the reports-only header above, and for the same
  // reason — one gutter rule, applied in one place, on both code paths. If only
  // the reports-only branch followed it, flipping REPORTS_ONLY_MODE off would
  // silently restore the misalignment.
  return (
    <header className="h-16 bg-white border-b border-gray-200 flex px-6 shrink-0">
      <div className="report-shell flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-display font-medium text-gray-900">{pageTitle}</h1>
          {site && (
            <span
              className="px-2 py-0.5 rounded-md bg-cb-light text-cb-navy text-xs font-medium"
              title={`Site: ${site}`}
            >
              {site}
            </span>
          )}
        </div>

        <div className="flex-1 max-w-lg mx-8" ref={searchRef}>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              data-search-input
              placeholder="Search matters, contacts, documents..."
              aria-label="Search matters, contacts, documents"
              role="combobox"
              aria-expanded={showResults}
              aria-controls="search-results"
              aria-autocomplete="list"
              aria-activedescendant={activeResultIndex >= 0 ? `search-result-${activeResultIndex}` : undefined}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setShowResults(true)
                setActiveResultIndex(-1)
              }}
              onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
              onKeyDown={handleSearchKeyDown}
              /* border-rc-control, not border-gray-200 — see the reports-only
                 search box above; this one sits on bg-gray-50, where #e5e7eb
                 measures 1.19:1, the worst control border found in the app. */
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-rc-control rounded-lg text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent transition-colors"
            />

            {/* Search Results Dropdown */}
            {showResults && searchTerm.length >= 2 && (
              <div id="search-results" role="listbox" className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 max-h-80 overflow-y-auto">
                {isFetching ? (
                  <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                ) : searchResults && searchResults.length > 0 ? (
                  searchResults.map((result, i) => (
                    <button
                      key={result.id || i}
                      id={`search-result-${i}`}
                      role="option"
                      aria-selected={i === activeResultIndex}
                      onClick={() => handleResultClick(result)}
                      className={`w-full text-left px-4 py-2.5 transition-colors flex items-center justify-between ${i === activeResultIndex ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{result.name}</p>
                      </div>
                      {result.type && (
                        <span className="text-xs text-gray-500 ml-3 shrink-0">{result.type}</span>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">No results found</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">{userMenu}</div>
      </div>
    </header>
  )
}

export default Header
